import { useEffect, useRef, useState } from "react";
import {
  ECG_RATE_HZ,
  EcgRing,
  PMD_CONTROL,
  PMD_DATA,
  PMD_SERVICE,
  START_ECG,
  decodeEcgFrame,
} from "@/shared/lib/polar-pmd";

export type HeartRateStatus = "unsupported" | "idle" | "connecting" | "connected" | "error";

export type SensorContact = "not-supported" | "not-detected" | "detected";

export type BodySensorLocation =
  | "other"
  | "chest"
  | "wrist"
  | "finger"
  | "hand"
  | "ear-lobe"
  | "foot"
  | "unknown";

export type HeartRateReading = Readonly<{
  bpm: number;
  rrIntervalsMs: ReadonlyArray<number>;
  receivedAt: number;
  batteryLevel?: number;
  contact: SensorContact;
  energyExpendedKj?: number;
}>;

export type DeviceInfo = Readonly<{
  manufacturer?: string;
  modelNumber?: string;
  serialNumber?: string;
  firmwareRevision?: string;
  hardwareRevision?: string;
  softwareRevision?: string;
  bodyLocation?: BodySensorLocation;
}>;

export type EcgState = Readonly<{
  active: boolean;
  ring: EcgRing;
  rateHz: number;
  tick: number;
  samples: number;
}>;

export type HeartRateSensorState = Readonly<{
  status: HeartRateStatus;
  deviceName: string | null;
  reading: HeartRateReading | null;
  deviceInfo: DeviceInfo;
  error: string | null;
  ecg: EcgState;
  connect: () => Promise<void>;
  disconnect: () => void;
}>;

const HEART_RATE_SERVICE = "heart_rate" as const;
const HEART_RATE_MEASUREMENT = "heart_rate_measurement" as const;
const BODY_SENSOR_LOCATION = "body_sensor_location" as const;
const BATTERY_SERVICE = "battery_service" as const;
const BATTERY_LEVEL = "battery_level" as const;
const DEVICE_INFORMATION_SERVICE = "device_information" as const;

const BODY_LOCATIONS: ReadonlyArray<BodySensorLocation> = [
  "other",
  "chest",
  "wrist",
  "finger",
  "hand",
  "ear-lobe",
  "foot",
];

type BluetoothWithGetDevices = Bluetooth & {
  getDevices?: () => Promise<Array<BluetoothDevice>>;
};

export function parseHeartRateMeasurement(value: DataView): Omit<HeartRateReading, "batteryLevel"> {
  const flags = value.getUint8(0);
  const is16Bit = (flags & 0x1) !== 0;
  const contactBits = (flags >> 1) & 0x3;
  const contact: SensorContact =
    contactBits < 0b10 ? "not-supported" : contactBits === 0b10 ? "not-detected" : "detected";
  const energyExpendedPresent = (flags & 0x8) !== 0;
  const hasRr = (flags & 0x10) !== 0;

  let offset = 1;
  const bpm = is16Bit ? value.getUint16(offset, true) : value.getUint8(offset);
  offset += is16Bit ? 2 : 1;

  let energyExpendedKj: number | undefined;
  if (energyExpendedPresent) {
    energyExpendedKj = value.getUint16(offset, true);
    offset += 2;
  }

  const rrIntervalsMs: Array<number> = [];
  if (hasRr) {
    while (offset + 2 <= value.byteLength) {
      const raw = value.getUint16(offset, true);
      rrIntervalsMs.push((raw / 1024) * 1000);
      offset += 2;
    }
  }

  const base = { bpm, rrIntervalsMs, receivedAt: Date.now(), contact };
  return energyExpendedKj === undefined ? base : { ...base, energyExpendedKj };
}

function decodeUtf8(value: DataView): string {
  return (
    new TextDecoder()
      .decode(new Uint8Array(value.buffer, value.byteOffset, value.byteLength))
      // oxlint-disable-next-line no-control-regex -- strip trailing NUL padding from BLE strings
      .replace(/\0+$/u, "")
      .trim()
  );
}

const DEVICE_INFO_FIELDS: ReadonlyArray<{ key: keyof DeviceInfo; uuid: string }> = [
  { key: "manufacturer", uuid: "manufacturer_name_string" },
  { key: "modelNumber", uuid: "model_number_string" },
  { key: "serialNumber", uuid: "serial_number_string" },
  { key: "firmwareRevision", uuid: "firmware_revision_string" },
  { key: "hardwareRevision", uuid: "hardware_revision_string" },
  { key: "softwareRevision", uuid: "software_revision_string" },
];

async function readDeviceInformation(
  server: BluetoothRemoteGATTServer,
): Promise<Partial<DeviceInfo>> {
  try {
    const service = await server.getPrimaryService(DEVICE_INFORMATION_SERVICE);
    const results = await Promise.all(
      DEVICE_INFO_FIELDS.map(async ({ key, uuid }) => {
        try {
          const char = await service.getCharacteristic(uuid);
          const value = await char.readValue();
          return [key, decodeUtf8(value)] as const;
        } catch {
          return null;
        }
      }),
    );
    const out: Partial<DeviceInfo> = {};
    for (const entry of results) {
      if (!entry) continue;
      const [key, value] = entry;
      if (value) (out as Record<string, string>)[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

async function readBodyLocation(
  hrService: BluetoothRemoteGATTService,
): Promise<BodySensorLocation | undefined> {
  try {
    const char = await hrService.getCharacteristic(BODY_SENSOR_LOCATION);
    const value = await char.readValue();
    const code = value.getUint8(0);
    return BODY_LOCATIONS[code] ?? "unknown";
  } catch {
    return undefined;
  }
}

export function useHeartRateSensor(): HeartRateSensorState {
  const supported = typeof navigator !== "undefined" && "bluetooth" in navigator;

  const [status, setStatus] = useState<HeartRateStatus>(supported ? "idle" : "unsupported");
  const [deviceName, setDeviceName] = useState<string | null>(null);
  const [reading, setReading] = useState<HeartRateReading | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({});
  const [error, setError] = useState<string | null>(null);

  const deviceRef = useRef<BluetoothDevice | null>(null);
  const characteristicRef = useRef<BluetoothRemoteGATTCharacteristic | null>(null);
  const batteryCharacteristicRef = useRef<BluetoothRemoteGATTCharacteristic | null>(null);
  const batteryLevelRef = useRef<number | null>(null);

  const ecgRingRef = useRef<EcgRing>(new EcgRing(ECG_RATE_HZ * 6));
  const pmdDataCharRef = useRef<BluetoothRemoteGATTCharacteristic | null>(null);
  const pmdControlCharRef = useRef<BluetoothRemoteGATTCharacteristic | null>(null);
  const ecgSamplesRef = useRef(0);
  const [ecg, setEcg] = useState<EcgState>({
    active: false,
    ring: ecgRingRef.current,
    rateHz: ECG_RATE_HZ,
    tick: 0,
    samples: 0,
  });

  const handleValueChanged = (event: Event): void => {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    if (!target.value) return;
    const parsed = parseHeartRateMeasurement(target.value);
    const battery = batteryLevelRef.current;
    setReading(battery !== null ? { ...parsed, batteryLevel: battery } : parsed);
  };

  const handleBatteryLevelChanged = (event: Event): void => {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    if (!target.value) return;
    const next = target.value.getUint8(0);
    batteryLevelRef.current = next;
    setReading((previous) => (previous ? { ...previous, batteryLevel: next } : previous));
  };

  const handlePmdData = (event: Event): void => {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    if (!target.value) return;
    const count = decodeEcgFrame(target.value, ecgRingRef.current);
    if (count > 0) {
      ecgSamplesRef.current += count;
      const samples = ecgSamplesRef.current;
      setEcg((previous) => ({ ...previous, tick: previous.tick + 1, samples }));
    }
  };

  const cleanup = (): void => {
    const characteristic = characteristicRef.current;
    if (characteristic) {
      characteristic.removeEventListener("characteristicvaluechanged", handleValueChanged);
      characteristic.stopNotifications().catch((caught: unknown) => {
        console.warn("HR sensor: stopNotifications failed", caught);
      });
      characteristicRef.current = null;
    }

    const batteryCharacteristic = batteryCharacteristicRef.current;
    if (batteryCharacteristic) {
      batteryCharacteristic.removeEventListener(
        "characteristicvaluechanged",
        handleBatteryLevelChanged,
      );
      batteryCharacteristic.stopNotifications().catch((caught: unknown) => {
        console.warn("HR sensor: battery stopNotifications failed", caught);
      });
      batteryCharacteristicRef.current = null;
    }

    const pmdData = pmdDataCharRef.current;
    if (pmdData) {
      pmdData.removeEventListener("characteristicvaluechanged", handlePmdData);
      pmdData.stopNotifications().catch(() => undefined);
      pmdDataCharRef.current = null;
    }
    pmdControlCharRef.current = null;

    const device = deviceRef.current;
    if (device?.gatt?.connected) device.gatt.disconnect();
    deviceRef.current = null;
  };

  const onDisconnected = (): void => {
    cleanup();
    setStatus("idle");
    setDeviceName(null);
    setReading(null);
    setDeviceInfo({});
    batteryLevelRef.current = null;
    ecgRingRef.current = new EcgRing(ECG_RATE_HZ * 6);
    ecgSamplesRef.current = 0;
    setEcg({
      active: false,
      ring: ecgRingRef.current,
      rateHz: ECG_RATE_HZ,
      tick: 0,
      samples: 0,
    });
  };

  const startPmdEcg = async (server: BluetoothRemoteGATTServer): Promise<void> => {
    try {
      const service = await server.getPrimaryService(PMD_SERVICE);
      const control = await service.getCharacteristic(PMD_CONTROL);
      const data = await service.getCharacteristic(PMD_DATA);
      await data.startNotifications();
      data.addEventListener("characteristicvaluechanged", handlePmdData);
      pmdControlCharRef.current = control;
      pmdDataCharRef.current = data;
      ecgRingRef.current = new EcgRing(ECG_RATE_HZ * 6);
      ecgSamplesRef.current = 0;
      await control.writeValueWithResponse(START_ECG);
      setEcg({
        active: true,
        ring: ecgRingRef.current,
        rateHz: ECG_RATE_HZ,
        tick: 0,
        samples: 0,
      });
    } catch (caught) {
      console.debug("[Polar PMD] ECG not available:", caught);
    }
  };

  const attachToDevice = async (device: BluetoothDevice): Promise<boolean> => {
    try {
      device.addEventListener("gattserverdisconnected", onDisconnected);
      const gatt = device.gatt;
      if (!gatt) return false;

      const server = await gatt.connect();
      const service = await server.getPrimaryService(HEART_RATE_SERVICE);
      const characteristic = await service.getCharacteristic(HEART_RATE_MEASUREMENT);
      await characteristic.startNotifications();
      characteristic.addEventListener("characteristicvaluechanged", handleValueChanged);

      deviceRef.current = device;
      characteristicRef.current = characteristic;

      const bodyLocation = await readBodyLocation(service);
      const info = await readDeviceInformation(server);
      setDeviceInfo(bodyLocation ? { ...info, bodyLocation } : info);

      try {
        const batteryService = await server.getPrimaryService(BATTERY_SERVICE);
        const batteryCharacteristic = await batteryService.getCharacteristic(BATTERY_LEVEL);
        await batteryCharacteristic.startNotifications();
        batteryCharacteristic.addEventListener(
          "characteristicvaluechanged",
          handleBatteryLevelChanged,
        );
        batteryCharacteristicRef.current = batteryCharacteristic;
        const initialValue = await batteryCharacteristic.readValue();
        const initialBattery = initialValue.getUint8(0);
        batteryLevelRef.current = initialBattery;
        setReading((previous) =>
          previous ? { ...previous, batteryLevel: initialBattery } : previous,
        );
      } catch {
        // Battery service unavailable — continue without it.
      }

      void startPmdEcg(server);

      setDeviceName(device.name ?? "Heart rate sensor");
      setStatus("connected");
      return true;
    } catch {
      return false;
    }
  };

  const connect = async (): Promise<void> => {
    if (!supported) return;

    setError(null);
    setStatus("connecting");

    const bluetooth = navigator.bluetooth as BluetoothWithGetDevices;
    const getDevices = bluetooth.getDevices;
    if (typeof getDevices === "function") {
      try {
        const known = await getDevices.call(bluetooth);
        for (const device of known) {
          if (await attachToDevice(device)) return;
        }
      } catch {
        // Fall through to chooser.
      }
    }

    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [HEART_RATE_SERVICE] }],
        optionalServices: ["battery_service", "device_information", PMD_SERVICE],
      });
      const attached = await attachToDevice(device);
      if (!attached) {
        cleanup();
        setStatus("error");
        setError("Failed to attach to heart rate sensor");
      }
    } catch (caught) {
      cleanup();
      const message = caught instanceof Error ? caught.message : String(caught);
      if (/cancel|user|chooser/i.test(message)) {
        setStatus("idle");
        setError(null);
      } else {
        setStatus("error");
        setError(message);
      }
    }
  };

  const disconnect = (): void => {
    onDisconnected();
  };

  useEffect(() => {
    if (!supported) return;

    let cancelled = false;
    const bluetooth = navigator.bluetooth as BluetoothWithGetDevices;
    const getDevices = bluetooth.getDevices;
    if (typeof getDevices !== "function") return;

    void (async () => {
      try {
        const devices = await getDevices.call(bluetooth);
        if (cancelled || devices.length === 0) return;

        setStatus("connecting");
        for (const device of devices) {
          if (cancelled) return;
          if (await attachToDevice(device)) return;
        }
        if (!cancelled) setStatus("idle");
      } catch {
        if (!cancelled) setStatus("idle");
      }
    })();
    return () => {
      cancelled = true;
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount; attachToDevice captured by closure
  }, [supported]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- runs once on unmount; cleanup stable
  useEffect(() => cleanup, []);

  return { status, deviceName, reading, deviceInfo, error, ecg, connect, disconnect };
}
